import { useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { MobileHeaderSlotContext } from "./Layout";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

export type MobileMenuItem = {
  key: string;
  label: string;
  icon?: ReactNode;
  badge?: string | number;
  active?: boolean;
  onSelect: () => void;
  testid?: string;
};

export function MobileMenu({
  currentLabel,
  items,
  onSignOut,
}: {
  currentLabel: string;
  items: MobileMenuItem[];
  // Sign-out is its own visually-distinct item at the bottom.
  onSignOut?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const slotEl = useContext(MobileHeaderSlotContext);

  // Esc closes the drawer; focus the close button when it opens.
  useEffect(() => {
    if (!open) return;
    closeBtnRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lock page scroll while the drawer is open so iOS doesn't bleed behind.
  useBodyScrollLock(open);

  // Title row lives inside the Layout's mobile header slot so the page
  // section (e.g. "Browse") and the hamburger sit on the same bar as the
  // DropIn logo — one combined top bar instead of two stacked ones.
  const titleRow = (
    <>
      <p
        className="text-sm font-semibold tracking-tightish text-ink truncate"
        data-testid="mobile-menu-current"
      >
        {currentLabel}
      </p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        aria-expanded={open}
        aria-controls="mobile-menu-drawer"
        data-testid="mobile-menu-trigger"
        className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-pill border border-border bg-surface text-ink shadow-xs active:scale-[0.97] transition-transform"
      >
        <HamburgerIcon />
      </button>
    </>
  );

  return (
    <>
      {slotEl ? createPortal(titleRow, slotEl) : null}

      {/* Backdrop + drawer — rendered via portal to escape any ancestor
          that creates a containing block for `position: fixed` (e.g. a
          backdrop-blur on the sticky tab bar). */}
      {open &&
        createPortal(
          <div className="fixed inset-0 z-[800]" aria-hidden={false}>
            {/* Backdrop — dark and solid enough to fully separate content. */}
            <button
              type="button"
              aria-label="Close menu"
              tabIndex={-1}
              onClick={() => setOpen(false)}
              className="absolute inset-0 bg-ink/70 animate-fade-in"
            />
            {/* Drawer panel — solid bg via both class and inline style. */}
            <aside
              id="mobile-menu-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Dashboard menu"
              className="absolute top-0 right-0 z-10 h-full w-[88%] max-w-[340px] bg-white border-l border-border shadow-lg pt-safe pb-safe flex flex-col animate-slide-in-right"
              style={{ backgroundColor: "#FFFFFF" }}
            >
              <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted">
                  Menu
                </p>
                <button
                  ref={closeBtnRef}
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-pill text-muted hover:text-ink hover:bg-paper"
                >
                  <CloseIcon />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-0.5">
                {items.map((it) => (
                  <button
                    key={it.key}
                    type="button"
                    onClick={() => {
                      it.onSelect();
                      setOpen(false);
                    }}
                    data-testid={it.testid}
                    aria-current={it.active ? "page" : undefined}
                    className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-semibold transition-colors ${
                      it.active
                        ? "bg-primary/10 text-primary"
                        : "text-ink hover:bg-paper"
                    }`}
                  >
                    <span
                      className={`shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md ${
                        it.active
                          ? "bg-primary text-white"
                          : "bg-paper text-muted"
                      }`}
                    >
                      {it.icon}
                    </span>
                    <span className="flex-1 truncate">{it.label}</span>
                    {it.badge !== undefined && it.badge !== "" && (
                      <span
                        className={`shrink-0 inline-flex items-center justify-center rounded-pill px-2 py-0.5 text-[11px] font-semibold ${
                          it.active
                            ? "bg-primary text-white"
                            : "bg-paper text-muted border border-border"
                        }`}
                      >
                        {it.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>

              {onSignOut && (
                <div className="border-t border-border px-3 py-3">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onSignOut();
                    }}
                    data-testid="menu-signout"
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-semibold text-rose hover:bg-rose/[0.06] transition-colors"
                  >
                    <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md bg-rose/10">
                      <LogoutIcon />
                    </span>
                    Sign out
                  </button>
                </div>
              )}
            </aside>
          </div>,
          document.body,
        )}
    </>
  );
}

function HamburgerIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

// Standard icons exported so the dashboard can pass them in as item.icon.
export function MapIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </svg>
  );
}

export function UserIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function TicketIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V9z" />
      <line x1="13" y1="5" x2="13" y2="7" />
      <line x1="13" y1="11" x2="13" y2="13" />
      <line x1="13" y1="17" x2="13" y2="19" />
    </svg>
  );
}
